import { ReactElement } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { usePageViewAnalytics } from '@client/analytics/hooks';
import { TEALIUM_PAGE_TYPE } from '@client/analytics/types/types';
import { useBreadcrumbs } from '@client/breadcrumbs/hooks';
import { RemoteRecordHandler } from '@client/common/components/RemoteRecordHandler';
import { isServer } from '@client/common/utils/env';
import { META_TAGS } from '@client/common/utils/seo/metaTags';
import { config } from '@client/config';
import { MetaTagsType } from '@client/content/components/ModularPage';
import { useTranslate } from '@client/i18n/hooks';
import { useManufacturerEffect } from '@client/manufacturer/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useUrlCreator } from '@client/routes/hooks';
import { getProductFallbackImgUrl } from '@client/routes/utils/url';
import { ViewPage } from './ViewPage';

export const ViewPageContainer = (): ReactElement => {
  const t = useTranslate();
  const { pathname } = useLocation();
  const urlCreator = useUrlCreator();
  const { manufacturer } = useParams<{ manufacturer: string }>();

  const manufacturerParam = manufacturer || '';
  const manufacturerId = manufacturerParam.substring(manufacturerParam.lastIndexOf('-') + 1);
  const manufacturerData = useManufacturerEffect(manufacturerId);
  const { isLoaded } = manufacturerData;

  const isManufacturerDataSpecified = !!manufacturerData;
  const shopName = config.shopName;
  const title = manufacturerData?.data?.name || '';
  const isImagePresent = !!manufacturerData?.data?.linkLogo;
  const cdnImageUrl = isImagePresent && manufacturerData?.data?.linkLogo;
  const imageUrl = cdnImageUrl || getProductFallbackImgUrl();
  const ogUrl = isServer ? `${config.url.root}${pathname}` : window.location.href;

  useBreadcrumbs([
    {
      title: t('Manufacturer support pages'),
      url: urlCreator(ROUTES.manufacturers),
    },
    ...(manufacturerData.isLoaded && manufacturerData.data ? [{
      title: `${manufacturerData.data.name} - ${manufacturerData.data.category}`,
    }] : []),
  ]);

  const metaTags: MetaTagsType = (!isLoaded || !isManufacturerDataSpecified)
    ? []
    : [
      {
        name: META_TAGS.ogImage,
        content: imageUrl,
      },
      {
        name: META_TAGS.ogTitle,
        content: title,
      },
      {
        name: META_TAGS.ogType,
        content: 'manufacturer',
      },
      {
        name: META_TAGS.ogUrl,
        content: ogUrl,
      },
      {
        name: META_TAGS.ogSitename,
        content: shopName,
      },
      {
        name: META_TAGS.title,
        content: title,
      },
      {
        name: META_TAGS.twitterCard,
        content: 'summary_large_image',
      },
      {
        name: META_TAGS.twitterImage,
        content: imageUrl,
      },
      {
        name: META_TAGS.twitterTitle,
        content: title,
      },
    ];


  usePageViewAnalytics({
    pageType: TEALIUM_PAGE_TYPE.brand,
    remoteRecord: manufacturerData,
  });

  return (
    <RemoteRecordHandler remoteRecord={manufacturerData}>
      <ViewPage
        manufacturer={manufacturerData}
        metaTags={metaTags}
      />
    </RemoteRecordHandler>
  );
};
